// src/utils/seo.js
// Utilitaires pour SEO et métadonnées - NE PAS MODIFIER

/**
 * Générer les métadonnées SEO pour une page
 * @param {object} config - Configuration SEO
 * @returns {object} Objet métadonnées complet
 */
export const generateSEOMetadata = (config) => {
  const {
    title = 'Fynho - Find Your New Home',
    description = 'Plateforme immobilière au Cameroun pour chercher logements, terrains et services',
    keywords = 'immobilier, Cameroun, logement, location, achat',
    image = '/images/og-default.jpg',
    url = 'https://fynho.com',
    type = 'website',
    language = 'fr',
    author = 'Fynho',
  } = config;

  return {
    title,
    description,
    keywords,
    image,
    url,
    type,
    language,
    author,
    charset: 'utf-8',
    viewport: 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes',
  };
};

/**
 * Générer le titre SEO optimisé pour une annonce
 * @param {object} listing - Objet annonce
 * @param {string} city - Ville
 * @returns {string} Titre optimisé
 */
export const generateListingTitle = (listing, city = '') => {
  const { title, type, quartier } = listing;
  const location = quartier ? `${quartier}, ${city}` : city;
  return `${title} - ${type} à ${location} | Fynho`;
};

/**
 * Générer la description SEO pour une annonce
 * @param {object} listing - Objet annonce
 * @returns {string} Description optimisée (max 160 caractères)
 */
export const generateListingDescription = (listing) => {
  const { title, quartier, price, bedrooms, bathrooms, category } = listing;
  let description = `${title} à ${quartier}. `;

  if (bedrooms) description += `${bedrooms} chambre(s), `;
  if (bathrooms) description += `${bathrooms} salle(s) de bain. `;
  if (price) description += `À partir de ${price} FCFA. `;

  description += 'Découvrez cette annonce sur Fynho.';

  // Limiter à 160 caractères
  return description.substring(0, 160);
};

/**
 * Générer des mots-clés SEO pour une annonce
 * @param {object} listing - Objet annonce
 * @param {string} city - Ville
 * @returns {string} Mots-clés séparés par des virgules
 */
export const generateListingKeywords = (listing, city = '') => {
  const keywords = [
    listing.type,
    listing.category,
    `${listing.type} à ${city}`,
    `location ${city}`,
    listing.quartier,
    'Cameroun',
  ];

  if (listing.amenities && listing.amenities.length > 0) {
    keywords.push(...listing.amenities.slice(0, 3));
  }

  return keywords.filter((k) => k).join(', ');
};

/**
 * Générer un slug SEO à partir d'un titre
 * @param {string} title - Titre
 * @param {string} quartier - Quartier
 * @param {number} id - ID de l'annonce
 * @returns {string} Slug formaté (ex: "appartement-3-chambres-bonapriso-1234")
 */
export const generateSlug = (title, quartier = '', id = '') => {
  let slug = title.toLowerCase();

  // Enlever les caractères spéciaux
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/[^\w\s-]/g, '');
  slug = slug.trim().replace(/\s+/g, '-');
  slug = slug.replace(/-+/g, '-');

  // Ajouter quartier et ID
  if (quartier) {
    const quartierSlug = quartier.toLowerCase().replace(/\s+/g, '-');
    slug += `-${quartierSlug}`;
  }

  if (id) {
    slug += `-${id}`;
  }

  return slug;
};

/**
 * Générer le Schema.org JSON-LD pour une annonce
 * @param {object} listing - Objet annonce
 * @param {string} url - URL complète de la page
 * @returns {object} Objet Schema.org
 */
export const generateListingSchema = (listing, url) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: listing.title,
    description: listing.description,
    url: url,
    image: listing.images && listing.images.length > 0
      ? `https://fynho.com${listing.images[0]}`
      : undefined,
    price: listing.price,
    priceCurrency: 'XOF',
    availabilityStarts: listing.createdAt,
  };

  // Ajouter l'adresse
  if (listing.quartier || listing.city) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: listing.quartier,
      addressRegion: listing.city || 'Cameroun',
      addressCountry: 'CM',
    };
  }

  // Ajouter les chambres/salles de bain si applicable
  if (listing.bedrooms) {
    schema.numberOfBedrooms = listing.bedrooms;
  }
  if (listing.bathrooms) {
    schema.numberOfBathrooms = listing.bathrooms;
  }

  // Ajouter la superficie
  if (listing.area) {
    schema.floorSize = {
      '@type': 'QuantitativeValue',
      value: listing.area,
      unitCode: 'MTK', // Square meter
    };
  }

  // Ajouter l'agent immobilier
  if (listing.ownerName) {
    schema.realEstateAgent = {
      '@type': 'RealEstateAgent',
      name: listing.ownerName,
      telephone: listing.ownerPhone,
    };
  }

  return schema;
};

/**
 * Générer le Schema.org JSON-LD pour une organisation
 * @returns {object} Objet Schema.org Organization
 */
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fynho',
    url: 'https://fynho.com',
    logo: 'https://fynho.com/images/logo.png',
    description: 'Plateforme immobilière camerounaise',
    sameAs: [
      'https://www.facebook.com/fynho',
      'https://www.instagram.com/fynho',
      'https://www.twitter.com/fynho',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      telephone: '+237640460565',
      email: 'support@fynho.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Douala',
      addressRegion: 'Littoral',
      addressCountry: 'CM',
    },
  };
};

/**
 * Générer le Schema.org JSON-LD pour une recherche locale
 * @param {string} query - Requête de recherche
 * @param {string} city - Ville
 * @returns {object} Objet Schema.org LocalBusiness
 */
export const generateLocalBusinessSchema = (query, city) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `Immobilier ${query} à ${city}`,
    description: `Trouvez ${query} à ${city} sur Fynho`,
    image: 'https://fynho.com/images/og-default.jpg',
    url: `https://fynho.com/search?q=${query}&city=${city}`,
    areaServed: {
      '@type': 'City',
      name: city,
      containedIn: {
        '@type': 'Country',
        name: 'Cameroon',
      },
    },
  };
};

/**
 * Créer une URL canonique
 * @param {string} path - Chemin de la page
 * @param {object} params - Paramètres de requête (si applicable)
 * @returns {string} URL canonique
 */
export const generateCanonicalUrl = (path, params = {}) => {
  const baseUrl = 'https://fynho.com';
  const url = new URL(`${baseUrl}${path}`);

  // Ajouter les paramètres pertinents pour le SEO
  if (params.city) url.searchParams.set('city', params.city);
  if (params.category) url.searchParams.set('category', params.category);
  if (params.type) url.searchParams.set('type', params.type);

  return url.toString();
};

/**
 * Générer les balises Open Graph pour le partage réseaux sociaux
 * @param {object} config - Configuration
 * @returns {object} Objet métadonnées Open Graph
 */
export const generateOpenGraphMeta = (config) => {
  const {
    title = 'Fynho',
    description = 'Trouvez votre prochain chez-vous',
    image = '/images/og-default.jpg',
    url = 'https://fynho.com',
    type = 'website',
  } = config;

  return {
    'og:title': title,
    'og:description': description,
    'og:image': image,
    'og:url': url,
    'og:type': type,
    'og:site_name': 'Fynho',
    'og:locale': 'fr_CM',
  };
};

/**
 * Générer les balises Twitter Card
 * @param {object} config - Configuration
 * @returns {object} Objet métadonnées Twitter
 */
export const generateTwitterMeta = (config) => {
  const {
    title = 'Fynho',
    description = 'Trouvez votre prochain chez-vous',
    image = '/images/og-default.jpg',
  } = config;

  return {
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image,
    'twitter:site': '@fynho',
  };
};

/**
 * Générer une sitemap XML en chaîne
 * @param {Array} listings - Tableau d'annonces
 * @param {Date} lastmod - Date de dernière modification
 * @returns {string} Sitemap XML
 */
export const generateSitemap = (listings = [], lastmod = new Date()) => {
  const baseUrl = 'https://fynho.com';
  const lastmodString = lastmod.toISOString().split('T')[0];

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

  // Pages principales
  const mainPages = [
    { url: '/', priority: '1.0', changefreq: 'daily' },
    { url: '/sejours', priority: '0.8', changefreq: 'daily' },
    { url: '/locations', priority: '0.8', changefreq: 'daily' },
    { url: '/immobilier', priority: '0.8', changefreq: 'daily' },
    { url: '/services', priority: '0.7', changefreq: 'weekly' },
  ];

  mainPages.forEach((page) => {
    xml += '  <url>\n';
    xml += `    <loc>${baseUrl}${page.url}</loc>\n`;
    xml += `    <lastmod>${lastmodString}</lastmod>\n`;
    xml += `    <changefreq>${page.changefreq}</changefreq>\n`;
    xml += `    <priority>${page.priority}</priority>\n`;
    xml += '  </url>\n';
  });

  // Ajouter les annonces
  listings.forEach((listing) => {
    if (listing.slug && listing.status === 'published') {
      xml += '  <url>\n';
      xml += `    <loc>${baseUrl}/listing/${listing.slug}</loc>\n`;
      xml += `    <lastmod>${lastmodString}</lastmod>\n`;
      xml += `    <changefreq>weekly</changefreq>\n`;
      xml += `    <priority>0.6</priority>\n`;
      xml += '  </url>\n';
    }
  });

  xml += '</urlset>';
  return xml;
};

/**
 * Générer un robots.txt
 * @returns {string} Contenu robots.txt
 */
export const generateRobotsTxt = () => {
  return `# Robots.txt pour Fynho
User-agent: *
Allow: /
Allow: /listing/
Allow: /sejours
Allow: /locations
Allow: /immobilier
Allow: /services
Disallow: /admin/
Disallow: /api/
Disallow: /admin-login

Sitemap: https://fynho.com/sitemap.xml

# Google
User-agent: Googlebot
Allow: /

# Bing
User-agent: Bingbot
Allow: /

# Rate limiting
User-agent: *
Crawl-delay: 1
`;
};

/**
 * Formater les métadonnées pour la balise head
 * @param {object} metadata - Objet métadonnées
 * @returns {Array} Tableau de balises meta
 */
export const formatMetaTags = (metadata) => {
  const tags = [
    { name: 'charset', content: metadata.charset || 'utf-8' },
    { name: 'viewport', content: metadata.viewport || 'width=device-width, initial-scale=1' },
    { name: 'description', content: metadata.description },
    { name: 'keywords', content: metadata.keywords },
    { name: 'author', content: metadata.author },
    { name: 'language', content: metadata.language || 'fr' },
  ];

  return tags.filter((tag) => tag.content);
};

/**
 * Vérifier si une URL est canonique
 * @param {string} url - URL à vérifier
 * @returns {boolean} true si l'URL est canonique
 */
export const isCanonicalUrl = (url) => {
  // URL sans paramètres de tracking
  return !url.includes('utm_') && !url.includes('fbclid') && !url.includes('gclid');
};

/**
 * Nettoyer une URL pour la canonicaliser
 * @param {string} url - URL à nettoyer
 * @returns {string} URL canonique
 */
export const canonicalizeUrl = (url) => {
  try {
    const urlObj = new URL(url);

    // Enlever les paramètres de tracking
    const trackingParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'fbclid',
      'gclid',
      'msclkid',
    ];

    trackingParams.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    // Trier les paramètres restants
    const params = Array.from(urlObj.searchParams.entries());
    params.sort((a, b) => a[0].localeCompare(b[0]));

    const canonicalUrl = new URL(urlObj.pathname, urlObj.origin);
    params.forEach(([key, value]) => {
      canonicalUrl.searchParams.append(key, value);
    });

    return canonicalUrl.toString();
  } catch (error) {
    return url;
  }
};

/**
 * Calculer le score SEO d'une page
 * @param {object} page - Objet page avec metadata
 * @returns {object} Score SEO et détails
 */
export const calculateSEOScore = (page) => {
  let score = 0;
  const checks = {
    title: { done: !!page.title && page.title.length > 10, points: 10 },
    description: { done: !!page.description && page.description.length > 50, points: 10 },
    keywords: { done: !!page.keywords && page.keywords.split(',').length >= 3, points: 10 },
    headings: { done: page.h1Count === 1, points: 10 },
    images: { done: page.images && page.images.length > 0, points: 10 },
    altText: { done: page.imagesWithAlt >= page.images?.length * 0.8, points: 10 },
    links: { done: page.internalLinks && page.internalLinks.length > 0, points: 10 },
    mobile: { done: page.isMobileOptimized, points: 10 },
    schema: { done: !!page.schema, points: 10 },
    speed: { done: page.loadTime < 3000, points: 10 }, // ms
  };

  Object.values(checks).forEach((check) => {
    if (check.done) score += check.points;
  });

  return {
    score: Math.min(100, score),
    percentage: `${Math.min(100, score)}%`,
    checks,
    level: score >= 80 ? 'Excellent' : score >= 60 ? 'Bon' : score >= 40 ? 'Moyen' : 'Faible',
  };
};